<script setup lang="ts">
//#region imports
import { watch, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useError } from '@/composables/useError';
import { useApi } from '@/api';
import type { PostFilesData } from '@/types/openapi-ts';
import type { ErrorValue } from '@/composables/useError';
import type { UploadedFile, Asset } from '@/types/core';
import type { UploadRequestHandler, UploadUserFile } from 'element-plus';
//#endregion

//#region types
interface Props {
  error?: ErrorValue
  disabled?: boolean
  limitFile?: number
  title?: string
  accept?: string
  isDragger?: boolean
  multiple?: boolean
  uploadFilesAction?: (options: Asset) => Promise<void>
  removeFile?: (file: UploadedFile) => void
}

interface  Emits {
  'is-loading': [value: boolean]
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  error: () => '',
  disabled: false,
  isDragger: false,
  multiple: false,
  limitFile: undefined,
  title: () => {
    const { t } = useI18n();
    return t('payments.uploadFilesPopup.title');
  },
  accept: () => '.pdf, .doc, .docx, .png, .jpg',
  uploadFilesAction: undefined,
  removeFile: undefined,
});
const emit = defineEmits<Emits>();
const modelValue = defineModel<UploadedFile[]>({
  default: () => [],
});
//#endregion

//#region composables
const api = useApi();
const { currentError } = useError({
  error: computed(() => {
    return props.error;
  }),
});
//#endregion

//#region state
const isFilesLoading = ref(false);
//#endregion

//#region methods
const handleUploadFilesAction: UploadRequestHandler = (options) => {
  if (props.uploadFilesAction) {
    return props.uploadFilesAction(options.file)
      .then(() => {
        options.onSuccess(null);
      })
      .catch((error) => {
        options.onError(error);
      });
  } else {
    isFilesLoading.value = true;
    const formData = new FormData();
    formData.append('files', options.file);
    formData.append('is_temporary', 'true');

    return api
      .postFiles(formData as unknown as PostFilesData['body'])
      .then((response) => {
        modelValue.value = [...modelValue.value, {
          name: options.file.name,
          uid: (options.file as any).uid,
          uploadId: response.data.payload?.files?.id,
        }];
        options.onSuccess(response.data);
      })
      .catch((error) => {
        options.onError(error);
      })
      .finally(() => {
        isFilesLoading.value = false;
      });
  }
};
const handleRemoveFile = (file: UploadedFile) => {
  if (props.removeFile) {
    props.removeFile(file);
  } else {
    const fileId = modelValue.value.findIndex(({ uid }) => uid === file.uid);

    if (fileId !== -1) {
      const fileList = [...modelValue.value];
      fileList.splice(fileId, 1);
      modelValue.value = fileList;
    }
  }
};
//#endregion

//#region watch
watch(isFilesLoading, (newValue) => {
  emit('is-loading', newValue);
});
//#endregion
</script>

<template>
  <div class="upload-file t:space-y-8">
    <UiLabel
      v-if="title"
      :label="title"
    />
    <div
      v-if="$slots.note"
      class="upload-file__note"
    >
      <slot name="note" />
    </div>
    <div class="upload-file__wrapper">
      <ElUpload
        v-loading="isFilesLoading"
        class=""
        :class="[
          'upload-file__upload',
          { 'upload-file__upload_disabled': disabled || (limitFile && modelValue.length >= limitFile) || isFilesLoading },
          { 'upload-file__upload_error': currentError },
          { 'upload-file__upload_dragger': isDragger }
        ]"
        :accept="accept"
        :drag="isDragger"
        action=""
        :file-list="(modelValue as unknown as UploadUserFile[])"
        :http-request="handleUploadFilesAction"
        :on-remove="handleRemoveFile"
        :auto-upload="true"
        :limit="limitFile"
        :multiple="multiple"
      >
        <template v-slot:trigger>
          <slot>
            <div class="upload-file__upload-help">
              <slot name="upload-text">
                {{ $t('crm.segments.manual.upload.dropFileHereOr') }} <em>{{ $t('crm.segments.manual.upload.selectFile') }}</em>
              </slot>
            </div>
          </slot>
        </template>
        <slot name="content" />

        <template v-slot:file="{ file }">
          <div
            :class="[
              'upload-file__file-container',
              { 'upload-file__file-container_loading': isFilesLoading }
            ]"
          >
            <div class="upload-file__file-container-block">
              <UiIcon
                color="var(--text-body)"
                substyle="far"
                name="file"
                class="icon-rows"
              />
              <div class="el-upload__tip">
                {{ file.name }}
              </div>
            </div>
            <div class="upload-file__file-container-block">
              <!--              у загруженного file есть file.id-->
              <UiAction
                v-if="!isFilesLoading && (file as any).id"
                color="var(--text-positive)"
                icon="check"
                class="upload-file__action upload-file__action_check"
              />
              <UiAction
                v-if="!isFilesLoading"
                icon="xmark"
                color="var(--text-negative)"
                class="upload-file__action upload-file__action_remove"
                @click="handleRemoveFile(file)"
              />
            </div>
          </div>
        </template>
      </ElUpload>
      <UiHelp
        v-if="currentError"
        is-error
        :text="currentError"
      />
    </div>
  </div>
</template>

<style lang="scss">
.upload-file{
  &__upload {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: flex-start;
    &_dragger {
      .el-upload {
        width: 100%;
      }
    }
    .el-upload-dragger {
      padding: 12px 0;
      border-radius: 5px;
      border: 1px dashed var(--border-focused);
      &:hover {
        border-color: var(--border-focused);
        background: var(--background-brand-subtle);
      }
      &.is-dragover {
        height: auto;
        padding: 12px 0;
        background: var(--background-brand-subtle);
        border: 1px dashed var(--border-focused);
      }
    }
    .el-upload-dragger:hover,
    .el-upload:focus .el-upload-dragger {
      border: 1px dashed var(--border-focused);
    }
    .el-upload-list {
      width: 100%;
      margin: 0;
    }
    &_disabled {
      .el-upload {
        cursor: not-allowed;
        filter: grayscale(100%);
        pointer-events: none;
      }
    }
    &_error {
      .el-upload-dragger {
        border-color: var(--border-negative);
      }
    }
  }
  &__upload-help {
    color: var(--text-body-secondary);
    text-align: center;
    font-size: 12px;
    font-weight: 400;
    em {
      color: var(--text-link);
    }
  }
  &__action{
    &_remove.ui-action {
      display: none;
    }
  }
  &__note {
    border-radius: 8px;
    background: var(--background-warning-subtle);
    padding: 16px;
    color: var(--text-body);
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
  }
  &__file-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 4px 8px;
    cursor: pointer;
    .el-upload__tip {
      margin-top: 0;
      margin-left: 8px;
      color: var(--text-body);
      padding-right: 30px;
      font-size: 12px;
    }
    &:hover {
      .upload-file__action{
        &_check {
          display: none;
        }
        &_remove {
          display: block;
        }
      }
    }
  }
  &__file-container-block {
    display: flex;
    align-items: center;
  }
}
</style>
